import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { fetchQuery, graphql } from "react-relay"
import { uniqBy } from "es-toolkit/array"
import { debounce } from "es-toolkit/function"
import { Input } from "@/components/ui/input"
import {
  CityData,
  CitySelectorProps,
  shouldTriggerCitySearch,
} from "@/modules/inbound-wizard/components/contact/address/city-selector"
import { SelectBoxWithSearch } from "@/modules/inbound-wizard/components/contact/address/selectbox-with-search"
import { emergencyCitySelectorCitySearchQuery } from "@/modules/patients/components/header/emergency-service/components/__generated__/emergencyCitySelectorCitySearchQuery.graphql"
import { RelayEnvironment } from "@/relay-environment"

export const EmergencyCitySelector = (props: CitySelectorProps) => {
  const { city, countryIsoCode, value, onChange, error, onSelect, ref, searchInputIdPrefix, required } = props
  const { t } = useTranslation()
  const testId = "patient-city"

  const [cities, setCities] = useState<ReadonlyArray<CityData>>([])

  const handleSearchCity = useCallback(async (keyword: string, countryIsoCode: string) => {
    const response = await fetchQuery<emergencyCitySelectorCitySearchQuery>(
      RelayEnvironment,
      graphql`
        query emergencyCitySelectorCitySearchQuery($countryIsoCode: String!, $keyword: String) {
          displayableCitySearch(countryIsoCode: $countryIsoCode, keyword: $keyword) {
            label
            value
            postalCode
          }
        }
      `,
      { countryIsoCode, keyword },
      { fetchPolicy: "store-or-network" },
    ).toPromise()

    setCities((previous) => response?.displayableCitySearch ?? previous)
  }, [])

  const debounceSearchCity = useRef(
    debounce((keyword: string, countryIsoCode: string) => {
      if (!shouldTriggerCitySearch(keyword)) return
      handleSearchCity(keyword, countryIsoCode)
    }, 250),
  ).current
  const isSwitzerlandSelected = countryIsoCode === "CH"

  useEffect(() => {
    if (isSwitzerlandSelected) {
      handleSearchCity("", countryIsoCode)
    } else {
      setCities([])
    }
  }, [countryIsoCode, handleSearchCity, isSwitzerlandSelected])

  const mergedOptions = city ? uniqBy(cities.concat(city), (item) => item.value) : cities

  useEffect(() => {
    if (countryIsoCode) {
      debounceSearchCity(value, countryIsoCode)
    }
  }, [value, countryIsoCode, debounceSearchCity])

  const errorMsg = t(error ?? "")
  if (!isSwitzerlandSelected)
    return (
      <Input
        ref={ref}
        wrapperProps={{ className: props.className }}
        label={t("common.city")}
        required={required}
        error={errorMsg}
        onChange={(e) => onChange?.(e.target.value)}
        value={value}
        testId={testId}
      />
    )

  return (
    <SelectBoxWithSearch
      ref={ref}
      className={props.className}
      type={searchInputIdPrefix ?? "city"}
      options={mergedOptions}
      value={value ?? ""}
      placeholder={t("common.city")}
      required={required}
      error={errorMsg}
      testId={testId}
      onChange={(option?: CityData) => {
        onChange?.(option?.value)
        onSelect(option)
      }}
      onSearchChange={(val) => {
        onChange?.(val)
        debounceSearchCity(val, countryIsoCode)
      }}
      renderOption={(option: CityData) => (
        <div className="grid grid-cols-1 py-0.5">
          <div className="text-body-sm text-on-surface block truncate">{option.label}</div>
          <div className="text-body-sm order-1 block truncate">{option.postalCode}</div>
        </div>
      )}
      ignoreLocalFilter
    />
  )
}
